/**
 * Secciones canónicas del plan UX v2 (2026-05-09).
 *
 * Cada proyecto en el sidebar se navega por 7 secciones generadoras + 1 chat libre.
 * Las instances se agrupan por `subtype` (Instance.subtype del backend) usando
 * match por prefijo — igual estrategia que el scope de endpoints por agente
 * (`agent-endpoint-scopes.ts`). Cubre todos los subtypes existentes en prod y
 * absorbe futuros sin tocar código.
 *
 * Documentación del plan: docs/PLAN_UX_V2_2026-05-09.md
 */

export type SectionKey =
  | 'personajes'
  | 'fondos'
  | 'panoramas'
  | 'composiciones'
  | 'series'
  | 'videos'
  | 'lipsync'
  | 'editor'
  | 'libre';

export interface SectionDef {
  key: SectionKey;
  emoji: string;
  label: string;
  /** Prefijos de `Instance.subtype` que caen en esta sección. */
  prefixes: readonly string[];
  /** Hint corto para mostrar bajo el contador en estado vacío. */
  hint: string;
}

export const SECTIONS: readonly SectionDef[] = [
  {
    key: 'personajes',
    emoji: '👤',
    label: 'Personajes',
    prefixes: ['character.'],
    hint: 'plantilla A1/A2/A3',
  },
  {
    key: 'fondos',
    emoji: '🏞',
    label: 'Fondos',
    prefixes: ['environment.'],
    hint: 'plantilla B1 + 9 estilos',
  },
  {
    key: 'panoramas',
    emoji: '🌐',
    label: 'Panoramas 360',
    prefixes: ['panorama.'],
    hint: '3 líneas en paralelo',
  },
  {
    key: 'composiciones',
    emoji: '🎭',
    label: 'Composiciones',
    prefixes: ['composition.'],
    hint: 'personaje + fondo armonizados',
  },
  {
    key: 'series',
    emoji: '🎞',
    label: 'Series de escenas',
    // Prefijo más largo que `video.`, así el índice por especificidad lo saca
    // de Videos y lo trae acá.
    prefixes: ['video.serie-escenas'],
    hint: 'varias tomas, aprobadas de a una',
  },
  {
    key: 'videos',
    emoji: '🎬',
    label: 'Videos',
    prefixes: ['video.'],
    hint: 'cámara · keyframes · loop',
  },
  {
    key: 'lipsync',
    emoji: '🗣️',
    label: 'Lipsync',
    prefixes: ['lipsync.'],
    hint: 'avatares parlantes',
  },
  {
    key: 'editor',
    emoji: '✨',
    label: 'Editor',
    // El Editor (no-IA, Remotion) emite `video.edited` o `image.animated`.
    // También captura `audio.*` (TTS huérfanos) hasta que tengamos su propia
    // sección Audio FX.
    prefixes: ['video.edited', 'image.animated', 'audio.'],
    hint: 'textos + audio (no-IA)',
  },
  {
    key: 'libre',
    emoji: '🧪',
    label: 'Chat libre',
    // Sandbox-chats del catálogo y todo lo que no matchee otra sección.
    prefixes: ['agent.sandbox-chat', 'agent.'],
    hint: 'hablar con cualquier experto',
  },
] as const;

const SECTION_BY_KEY: Record<SectionKey, SectionDef> = Object.fromEntries(
  SECTIONS.map((s) => [s.key, s]),
) as Record<SectionKey, SectionDef>;

// Index plano de (prefijo → sección) ordenado por longitud descendente. Garantiza
// que un subtype específico (`video.edited`) matchee al Editor antes que al
// prefijo más corto (`video.`) de Videos, sin importar el orden del array
// `SECTIONS` (que define el orden visual del sidebar, no la prioridad de match).
const PREFIX_INDEX: ReadonlyArray<readonly [string, SectionKey]> = SECTIONS.flatMap(
  (s) => s.prefixes.map((p) => [p, s.key] as const),
).sort((a, b) => b[0].length - a[0].length);

/**
 * Devuelve la sección a la que pertenece un `subtype` dado.
 *
 * - `null` o vacío → `'libre'` (legacy sin subtype).
 * - El prefijo más específico gana (más largo primero).
 * - Si nada matchea (subtype nuevo no contemplado) → `'libre'`, para que la
 *   instance no desaparezca del sidebar mientras se decide su sección.
 */
export function getSectionForSubtype(subtype: string | null | undefined): SectionKey {
  if (!subtype) return 'libre';
  for (const [prefix, key] of PREFIX_INDEX) {
    if (subtype === prefix || subtype.startsWith(prefix)) return key;
  }
  return 'libre';
}

export function getSectionDef(key: SectionKey): SectionDef {
  return SECTION_BY_KEY[key];
}
